import { useEffect, useState } from "react";
import { CalendarCheck, ClipboardText, Table, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AttendanceMatrix from "./AttendanceMatrix";

const CLASS_OPTIONS = ["Tim Hijau (Pemula)", "Tim Kuning", "Tim Jingga", "Tim Biru", "Tim Pink", "Tim Merah", "Speed", "Slalom"];
const STATUS_OPTIONS = ["hadir", "izin", "sakit", "alpha"];

export default function AttendanceTab() {
    const { user } = useAuth();
    const isFinance = user?.role === "finance";
    const [view, setView] = useState(isFinance ? "matrix" : "harian");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [className, setClassName] = useState("Tim Hijau (Pemula)");
    const [note, setNote] = useState("");
    const [athletes, setAthletes] = useState([]);
    const [statuses, setStatuses] = useState({});
    const [sessions, setSessions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [recapConfirm, setRecapConfirm] = useState(false);

    const load = async () => {
        try {
            const [athleteRes, sessionRes, allSessions] = await Promise.all([
                api.get(`/coach/athletes?class_name=${encodeURIComponent(className)}`),
                api.get(`/attendance/sessions?date=${date}&class_name=${encodeURIComponent(className)}`),
                api.get("/attendance/sessions"),
            ]);
            setAthletes(athleteRes.data);
            const existing = sessionRes.data[0];
            setNote(existing?.note || "");
            setStatuses(Object.fromEntries(athleteRes.data.map((a) => {
                const record = existing?.records?.find((r) => r.athlete_id === a.id);
                return [a.id, record?.status || "hadir"];
            })));
            setSessions(allSessions.data.slice(0, 10));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    useEffect(() => {
        if (!isFinance) load();
    }, [date, className]);

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/attendance/sessions", {
                date,
                class_name: className,
                note,
                records: athletes.map((athlete) => ({ athlete_id: athlete.id, status: statuses[athlete.id] || "hadir", note: "" })),
            });
            toast.success("Absensi tersimpan");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const completeDay = async (force = false) => {
        setCompleting(true);
        try {
            const { data } = await api.post("/attendance/day-complete", { date, force });
            const summary = Object.entries(data.per_class || {})
                .map(([cls, c]) => `${cls}: ${c.hadir} hadir`)
                .join(" • ");
            toast.success(`${data.message} (${summary})`, { duration: 6000 });
            load();
        } catch (err) {
            if (err.response?.status === 409) {
                setRecapConfirm(true);
            } else {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
            }
        } finally {
            setCompleting(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="attendance-tab">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h2 className="font-display uppercase text-2xl">Absensi Pertemuan</h2>
                    <p className="text-sm text-racing-muted mt-2">{isFinance ? "Review absensi yang sudah dibuat pelatih — tabel rekap kehadiran per kelas." : "Pelatih memilih tanggal dan kelas, lalu menandai status setiap atlet."}</p>
                </div>
                <div className="flex items-end gap-3">
                    {!isFinance && (
                    <div className="flex border border-racing-line" data-testid="attendance-view-toggle">
                        <button onClick={() => setView("harian")} data-testid="attendance-view-harian" className={`px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors ${view === "harian" ? "bg-racing-red text-white" : "text-racing-muted hover:text-neutral-900"}`}>Absen Harian</button>
                        <button onClick={() => setView("matrix")} data-testid="attendance-view-matrix" className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors ${view === "matrix" ? "bg-racing-red text-white" : "text-racing-muted hover:text-neutral-900"}`}><Table size={15} /> Rekap Ceklist</button>
                    </div>
                    )}
                    {view === "harian" && (
                        <div className="flex gap-2">
                            <a href={`${api.defaults.baseURL}/attendance/export?date=${date}&class_name=${encodeURIComponent(className)}&format=xlsx`} data-testid="attendance-export-xlsx" className="border border-racing-line px-4 py-3 text-xs uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red">Excel</a>
                            <a href={`${api.defaults.baseURL}/attendance/export?date=${date}&class_name=${encodeURIComponent(className)}&format=pdf`} data-testid="attendance-export-pdf" className="border border-racing-line px-4 py-3 text-xs uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red">PDF</a>
                        </div>
                    )}
                </div>
            </div>

            {view === "matrix" && <AttendanceMatrix />}

            {view === "harian" && (
            <>
            <section className="bg-white border border-racing-line p-6 grid md:grid-cols-4 gap-4">
                <div><label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tanggal</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date" className="w-full bg-racing-surface border border-racing-line px-3 py-3 text-sm" /></div>
                <div><label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas</label><select value={className} onChange={(e) => setClassName(e.target.value)} data-testid="attendance-class" className="w-full bg-racing-surface border border-racing-line px-3 py-3 text-sm">{CLASS_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select></div>
                <div className="md:col-span-2"><label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Catatan Pertemuan</label><input value={note} onChange={(e) => setNote(e.target.value)} data-testid="attendance-note" className="w-full bg-racing-surface border border-racing-line px-3 py-3 text-sm" placeholder="Materi/catatan latihan" /></div>
            </section>

            <section className="bg-white border border-racing-line">
                <div className="p-5 border-b border-racing-line flex flex-wrap items-center justify-between gap-3"><span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-racing-muted"><Users size={16} /> {athletes.length} atlet</span><div className="flex flex-wrap gap-2"><button onClick={save} disabled={saving || athletes.length === 0} data-testid="attendance-save" className="bg-racing-red text-white px-5 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan Absensi"}</button><button onClick={() => completeDay(false)} disabled={completing} data-testid="attendance-complete-day" className="border border-racing-gold text-racing-golddeep hover:bg-racing-gold/10 px-5 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">{completing ? "Mengirim..." : "Selesai Latihan & Kirim Rekap WA"}</button></div></div>
                <div className="divide-y divide-racing-line">
                    {athletes.length === 0 && <p className="p-8 text-center text-racing-muted" data-testid="attendance-empty">Belum ada atlet aktif pada kelas ini.</p>}
                    {athletes.map((athlete, index) => (
                        <div key={athlete.id} className="p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`attendance-athlete-${index}`}>
                            <div className="flex items-center gap-3 flex-1">
                                {athlete.photo_url ? <img src={fileUrl(athlete.photo_url)} alt="" className="w-11 h-11 object-cover border border-racing-line" /> : <div className="w-11 h-11 bg-racing-surface border border-racing-line" />}
                                <div><p className="font-medium">{athlete.name}</p><p className="text-xs text-racing-muted">{athlete.nickname || athlete.parent_username}</p></div>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                {STATUS_OPTIONS.map((status) => (
                                    <button key={status} onClick={() => setStatuses({ ...statuses, [athlete.id]: status })} data-testid={`attendance-${status}-${index}`} className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] border transition-colors ${statuses[athlete.id] === status ? "bg-racing-red border-racing-red text-white" : "border-racing-line text-racing-muted hover:border-racing-red"}`}>{status}</button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {recapConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="attendance-recap-confirm">
                    <div className="bg-white border border-racing-line max-w-md w-full p-6">
                        <h3 className="font-display uppercase text-xl">Rekap Sudah Terkirim</h3>
                        <p className="text-sm text-racing-muted mt-2">Rekap latihan tanggal ini sudah pernah dikirim ke WhatsApp. Kirim ulang hanya jika Anda baru saja mengoreksi absensi.</p>
                        <div className="flex gap-3 mt-5">
                            <button onClick={() => { setRecapConfirm(false); completeDay(true); }} data-testid="attendance-recap-resend" className="flex-1 bg-racing-red text-white text-xs uppercase tracking-[0.2em] py-3">Kirim Ulang</button>
                            <button onClick={() => setRecapConfirm(false)} data-testid="attendance-recap-cancel" className="flex-1 border border-racing-line text-racing-muted text-xs uppercase tracking-[0.2em] py-3">Batal</button>
                        </div>
                    </div>
                </div>
            )}

            <section>
                <h3 className="flex items-center gap-2 font-display uppercase text-xl mb-4"><ClipboardText size={20} /> Riwayat Terbaru</h3>
                <div className="grid md:grid-cols-2 gap-4">
                    {sessions.map((session, index) => (
                        <div key={session.id} className="bg-white border border-racing-line p-5" data-testid={`attendance-session-${index}`}>
                            <p className="text-xs uppercase tracking-[0.2em] text-racing-golddeep">{session.date}</p>
                            <h4 className="font-display uppercase text-xl mt-2">{session.class_name}</h4>
                            <p className="text-sm text-racing-muted mt-2">Hadir {session.records.filter((r) => r.status === "hadir").length} dari {session.records.length} atlet • Coach {session.coach_name}</p>
                        </div>
                    ))}
                </div>
            </section>
            </>
            )}
        </div>
    );
}
